// Character-owned cosmetic state. Never read the retired viewer-local preference.
import { appearanceHelmetState, encodeCreatureAppearance } from '../janus-engine/authoring/creature-appearance';
import { cardEditFetch } from './card-edit-publication';
import { withAsyncDeadline } from './async-deadline';
import { defaultHoodState, hoodComponentVisible, hoodPartState, isHoodGarment, drawnHoodRaised, type HoodState } from '../../../ft3d/engine/hood-state.js';
export { hoodState } from '../../../ft3d/engine/hood-state.js';
const normalize = (id: string): string => id.trim().toUpperCase();
const pending = new Set<string>();

export type HelmetState = 'closed' | 'open' | 'off';

export function helmetState(appearance: unknown): HelmetState {
    return appearanceHelmetState(appearance);
}

export function helmetHidden(appearance: unknown): boolean {
    return helmetState(appearance) === 'off';
}

export function helmetStateControl(id: string, state: HelmetState, editable: boolean): string {
    if (!id || !/^[A-Z0-9-]+$/i.test(id)) return '';
    const options = (['closed', 'open', 'off'] as const).map(value => `<option value="${value}"${value === state ? ' selected' : ''}>${value.charAt(0).toUpperCase() + value.slice(1)}</option>`).join('');
    return `<select data-ft-helmet-state="${normalize(id)}" data-ft-helmet-saved-state="${state}" data-ft-helmet-editable="${editable}" aria-label="Helmet" style="position:absolute;top:100%;left:50%;transform:translateX(-50%);font-size:10px;line-height:14px;cursor:pointer"${!editable || pending.has(normalize(id)) ? ' disabled' : ''}>${options}</select>`;
}

export function hoodStateControl(id: string, state: HoodState, editable: boolean): string {
    if (!id || !/^[A-Z0-9-]+$/i.test(id)) return '';
    const options = (['up', 'down'] as const).map(value => `<option value="${value}"${value === state ? ' selected' : ''}>Hood ${value === 'up' ? 'Up' : 'Down'}</option>`).join('');
    return `<select data-ft-hood-state="${normalize(id)}" data-ft-hood-saved-state="${state}" data-ft-hood-editable="${editable}" aria-label="Hood state" class="form-select form-select-sm" style="width:auto;margin:6px auto"${!editable || pending.has(normalize(id) + ':hood') ? ' disabled' : ''}>${options}</select>`;
}

/** Keep the lowered drape/common garment; only the alternative hood components switch. */
export function bindHoodState(root: any, state: HoodState | null = null): void {
    const meshes: any[] = [];
    root.traverse((node: any) => { if (node.isMesh && isHoodGarment(node)) meshes.push(node); });
    const fallback = defaultHoodState(meshes);
    for (const mesh of meshes) {
        if (!hoodPartState(mesh)) continue;
        mesh.userData ??= {};
        if (mesh.userData.ftHoodOriginalVisible === undefined) mesh.userData.ftHoodOriginalVisible = mesh.visible;
        mesh.visible = mesh.userData.ftHoodOriginalVisible && hoodComponentVisible(mesh, state);
    }
    root.userData ??= {};
    root.userData.ftHoodState = state ?? fallback;
    root.userData.ftHoodRaised = drawnHoodRaised(meshes);
}

/** Explicit component metadata and authored names cover integrated armor helmets. */
export function bindHelmetState(_id: string, root: any, state: HelmetState = 'closed', THREE: any = null): void {
    root.userData ??= {};
    root.userData.ftHelmetState = state;
    root.userData.ftHelmetHidden = state === 'off';
    const helmets: any[] = [];
    root.traverse((node: any) => {
        if (!node.isMesh) return;
        const data = node.userData ?? {};
        // Hood face openings leave facial hair exposed. Scalp hair is covered separately below;
        // a coarse hood bounding box must not treat the open face as an enclosed helmet.
        if (isHoodGarment(node)) return;
        if (data.ftComponent === 'suit.gorget') return;
        const names = [data.ftHelmetComponentName, data.name, node.name];
        const faceplate = data.ftComponent === 'helmet.faceplate' || data.ft_helmet_part === 'faceplate'
            || names.some(name => /(?:^|[_. -])(visor|faceplate)(?:[_. -]|$)/i.test(String(name ?? '')));
        if (data.ftEquipmentCell === 'head' || data.ft_optional_component === 'cowl'
            || data.ft_authored_component === 'Helmet' || data.ftComponent === 'helmet.shell' || faceplate
            || names.some(name => /(?:^|[_. -])Helmet(?:[_. -]|$)/i.test(String(name ?? '')))) {
            if (data.ftHelmetOriginalVisible === undefined) data.ftHelmetOriginalVisible = node.visible;
            node.userData = data;
            const hoodCoversHelmet = root.userData.ftHoodRaised === true && data.ft_layer_role !== 'head_accessory';
            node.visible = state === 'off' || (state === 'open' && faceplate) || hoodCoversHelmet ? false : data.ftHelmetOriginalVisible;
            let drawn = node.visible;
            for (let parent = node.parent; drawn && parent && parent !== root; parent = parent.parent) drawn = parent.visible;
            // A head accessory (circlet, crown, eyewear — the fitting's mesh-extras tag) is worn over the hair, never
            // around it: a crown's box spans the scalp, so the enclosure test would otherwise hide short hair under it.
            if (drawn && data.ft_layer_role !== 'head_accessory') helmets.push(node);
        }
    });
    if (THREE) {
        const started = performance.now();
        hideHairInsideHelmet(THREE, root, helmets);
        root.userData.ftHelmetHairMs = Math.round((performance.now() - started) * 10) / 10;
    }
}

export function bindHelmetVisibility(id: string, root: any, hidden = false, THREE: any = null): void {
    bindHelmetState(id, root, hidden ? 'off' : 'closed', THREE);
}

/** The avatar export's own scalp-hair (`ft.hair.*`, `fthair*`) and facial-hair (`*facialhair*`) nodes.
 * Node identity, never material names: a combined head mesh can carry a hair material. */
const HAIR_NODE_PATTERN = /^ft\.?hair|facial_?hair/i;
/** Share of a hair mesh's vertices that must sit inside the helmet for the helmet to own it. */
const ENCLOSED_SHARE = 0.8;

/**
 * Hair or facial hair ENCLOSED by a drawn helmet is hidden. Left on, it pokes through the visor and
 * faceplate as the head animates, which reads as flicker (2026-09-22: Fallen Knight great helm over
 * beard-24, 2–7 px per frame at 480×360, jumping frame to frame). Measured from the geometry itself
 * rather than a closed/open flag, so an open helm keeps a beard that hangs below it. Re-derived on every
 * bind, so switching the helmet off gives the hair back.
 */
function hideHairInsideHelmet(THREE: any, root: any, helmets: readonly any[]): void {
    const hairMeshes: any[] = [];
    root.traverse((node: any) => {
        // Rigid only: the avatar's hair and beard ride the head bone; a skinned match would be body or garment.
        if (!node.isMesh || node.isSkinnedMesh || node.userData?.ftEquipmentCell || helmets.includes(node)) return;
        if (HAIR_NODE_PATTERN.test(String(node.userData?.name ?? '')) || HAIR_NODE_PATTERN.test(String(node.name ?? ''))) hairMeshes.push(node);
    });
    if (!hairMeshes.length) return;
    root.updateMatrixWorld(true);
    const box = new THREE.Box3();
    for (const helmet of helmets) box.union(new THREE.Box3().setFromObject(helmet, true));
    const vertex = new THREE.Vector3();
    for (const mesh of hairMeshes) {
        mesh.userData ??= {};
        if (mesh.userData.ftHelmetHairOriginalVisible === undefined) mesh.userData.ftHelmetHairOriginalVisible = mesh.visible;
        const original = mesh.userData.ftHelmetHairOriginalVisible;
        let enclosed = false;
        const count = mesh.geometry?.attributes?.position?.count ?? 0;
        if (helmets.length && !box.isEmpty() && count > 0) {
            const step = Math.max(1, Math.ceil(count / 4000));
            let inside = 0, sampled = 0;
            for (let i = 0; i < count; i += step) {
                mesh.getVertexPosition(i, vertex).applyMatrix4(mesh.matrixWorld);
                sampled++;
                if (box.containsPoint(vertex)) inside++;
            }
            enclosed = inside / sampled >= ENCLOSED_SHARE;
        }
        mesh.userData.ftHelmetHairEnclosed = enclosed;
        const scalpUnderHood = root.userData.ftHoodRaised === true
            && !/facial_?hair/i.test(String(mesh.userData?.name ?? mesh.name ?? ''));
        mesh.visible = enclosed || scalpUnderHood ? false : original;
    }
}

document.addEventListener('change', async event => {
    const input = event.target;
    if (!(input instanceof HTMLSelectElement) || !input.matches('select[data-ft-helmet-state],select[data-ft-hood-state]')) return;
    const hood = input.dataset.ftHoodState !== undefined;
    const id = normalize((hood ? input.dataset.ftHoodState : input.dataset.ftHelmetState) ?? '');
    const pendingKey = id + (hood ? ':hood' : '');
    if (pending.has(pendingKey)) return;
    const state = input.value;
    if (hood ? state !== 'up' && state !== 'down' : state !== 'closed' && state !== 'open' && state !== 'off') return;
    const previous = (hood ? input.dataset.ftHoodSavedState : input.dataset.ftHelmetSavedState) ?? (hood ? 'down' : 'closed');
    pending.add(pendingKey);
    const controls = () => Array.from(document.querySelectorAll<HTMLSelectElement>(hood ? 'select[data-ft-hood-state]' : 'select[data-ft-helmet-state]'))
        .filter(control => normalize((hood ? control.dataset.ftHoodState : control.dataset.ftHelmetState) ?? '') === id);
    const sync = (value: string) => {
        for (const control of controls()) {
            control.value = value;
            if (hood) control.dataset.ftHoodSavedState = value;
            else control.dataset.ftHelmetSavedState = value;
        }
    };
    for (const control of controls()) { control.value = state; control.disabled = true; }
    try {
        // This route writes only equipment fields into the locked saved document.
        // Validate that narrow document without requiring a creator body or head.
        const visibility = JSON.parse(encodeCreatureAppearance({ schema: 'ft.creature-equipment-appearance.v1',
            equipmentVisibility: hood ? { hoodState: state } : { helmetState: state, hideHelmet: state === 'off' } })).equipmentVisibility;
        const token = await withAsyncDeadline(async signal => {
            const tokenResponse = await fetch('/options/api/helmet_visibility_inc.php', { credentials: 'same-origin', cache: 'no-store', signal });
            const token = await tokenResponse.json();
            if (!tokenResponse.ok || !token.ok) throw new Error('Could not load the headwear setting.');
            return token;
        }, 6000).catch(error => { throw new Error(error?.name === 'TimeoutError' ? 'card-edit:headwear-token-timeout' : String(error)); });
        const response = await cardEditFetch('/options/api/helmet_visibility_inc.php', {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subject_id: id, [hood ? 'hood_state' : 'helmet_state']: hood ? visibility.hoodState : visibility.helmetState, csrf_token: token.csrf_token }),
        });
        const result = await response.json();
        if (!response.ok || !result.ok) throw new Error(result.error || 'Could not save the headwear setting.');
        sync(hood ? result.hood_state : helmetState({ equipmentVisibility: { helmetState: result.helmet_state, hideHelmet: result.hidden } }));
    } catch (error) {
        sync(previous);
        window.alert(error instanceof Error ? error.message : 'Could not save the helmet setting.');
    } finally {
        pending.delete(pendingKey);
        for (const control of controls()) control.disabled = (hood ? control.dataset.ftHoodEditable : control.dataset.ftHelmetEditable) !== 'true';
    }
});
